<template>
  <div
    class="main-product critical-component-clear adsfasfdds"
    :class="[
      { [$class('loading')]: state === 'loading' },
      typeClass
    ]"
  >
    <breadcrumbs
      :ancestor-title="liquid?.collection?.title"
      :ancestor-url="liquid?.collection?.url"
      :state="state"
      :title="productObject.title"
      class="main-product__breadcrumbs"
    />
    <div class="grid">
      <div
        id="product-gallery"
        class="col xs-span l6"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="horizontal-vertical"
          :loading="state === 'loading'"
          :product="productObject"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div class="col xs-span l7-11 main-product__right-container this-is-zero">
        <div class="main-product__details">
          <div>
            <h1
              id="template-title"
              class="
              main-product__title
              text-heading-4xl
              text-heading-5xl-desktop
              this-is-vue-dev-file
            "
              v-text="productObject.title"
            />

            <p
              v-if="productObject.shortDescription"
              class="main-product__short-description text-body-s-regular"
              v-text="productObject.shortDescription"
            />
          </div>

          <div class="main-product__description text-body-s-regular">
            <p
              class="text-body-s-regular"
              v-html="formattedDescription"
            />

            <button
              v-if="showMore"
              type="button"
              class="text-body-s-underline"
              @click="showFullDescription = !showFullDescription"
              v-text="readText"
            />
          </div>

          <div
            v-if="productObject.tasteNotes"
            class="main-product__taste"
          >
            <taste-notes
              :notes="productObject.tasteNotes.slice(0, 3)"
            />

            <button
              v-if="hasDetailsSection"
              type="button"
              class="text-body-xs-underline"
              @click="scrollToSection"
              v-text="$string('product.learn_more')"
            />
          </div>
        </div>

        <product-form
          class="main-product__form"
          :loading="state === 'loading'"
          :product="productObject"
          :section-id="sectionId"
          :settings="settings"
          :variant="variant"
          @update-variant="handleVariantUpdate"
        />

        <div class="main-product__delivery">
          <icon-shipping />

          <div class="text-body-xs-regular">
            {{ $string('product.delivery.text') }}

            <button
              type="button"
              class="text-body-xs-underline"
              v-text="$string('product.delivery.button')"
            />
          </div>
        </div>

        <div class="main-product__additional-data">
          <template
            v-for="key of Object.keys(productObject?.additionalData)"
            :key="key"
          >
            <div
              v-if="productObject?.additionalData[key]"
              class="main-product__additional-data--item"
            >
              <p
                class="text-heading-m"
                v-text="$string(`product.additional_data.${key}`)"
              />

              <p
                class="text-body-s-regular"
                v-html="productObject?.additionalData[key]"
              />
            </div>
          </template>

          <div
            v-if="productObject.giftCard"
            class="main-product__additional-description"
          >
            <p
              class="text-heading-m"
              v-text="$string('product.form.how_it_works')"
            />

            <p
              class="text-body-s-regular"
              v-html="productObject?.additionalDescription"
            />
          </div>
        </div>

        <product-grades
          v-if="productObject.grades.length"
          class="main-product__grades"
          :loading="loading"
          :product="productObject"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Boolean|String} [handle] - Product handle to load (Storybook).
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} sectionId - Section ID.
 * @param {Object} settings - Product form settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import TasteNotes from '~async/taste-notes/taste-notes'
import ProductGrades from '~async/product-form/product-grades'

import IconShipping from '~icons/general/shipping.svg'

import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/format/format-product'
import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    Breadcrumbs,
    ProductForm,
    ProductGrades,
    ProductGallery,
    TasteNotes,
    IconShipping,
  },

  props: {
    handle: {
      type: [Boolean, String],
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    sectionId: {
      type: String,
      default: '',
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      controller: false,
      currentIndex: 0,
      currentNamespace: 'gallery',
      state: 'loading',
      showFullDescription: false,
    }
  },

  computed: {

    /**
     * Compute local handle from prop or page handle.
     * @returns {Boolean|String}
     */
    localHandle() {
      if (this.$variable('storybook') && this.handle === false) {
        return false
      }

      return this.handle || this.$variable('page.handle')
    },

    /**
     * Compute product object.
     * @returns {Object}
     */
    productObject() {
      return this.product || this.liquid
    },

    /**
     * Compute variant object.
     * @returns {Object}
     */
    variantObject() {
      return this.variant
        ? this.variant
        : this.productObject.selectedOrFirstAvailableVariant
    },

    /**
     * Compute vendor collection URL.
     * @returns {String}
     */
    vendorCollectionUrl() {
      return this.productObject.vendor
        ? `${this.$variable('routes.normalisedRoot')}collections/vendors?q=${encodeURI(this.productObject.vendor)}`
        : ''
    },

    /**
     * Returns type class.
     * @returns {String}
     */
    typeClass() {
      return this.productObject?.type?.toLowerCase().replaceAll(' ', '-')
    },

    /**
     * Returns true if product details section exist.
     * @returns {Boolean}
     */
    hasDetailsSection() {
      return Boolean(document.getElementById('product-details'))
    },

    /**
     * Compute product description.
     * @returns {String}
     */
    formattedDescription() {
      if (!this.product) {
        return ''
      }

      const descriptionText = this.product.description.replace(/<(?!br\s*\/?)[^>]+>/gm, '')

      if (this.showFullDescription || !descriptionText.length > 350) {
        return descriptionText
      }

      return `${descriptionText.slice(0, 350).trim()}...`
    },

    showMore() {
      return this.product?.description ? this.product?.description.replace(/<(?!br\s*\/?)[^>]+>/gm, '').length > 350 : false
    },

    /**
     * Returns read text.
     * @returns {String}
     */
    readText() {
      return this.showFullDescription
        ? this.$string('product.read_less')
        : this.$string('product.read_more')
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.localHandle)
    },
  },

  mounted() {
    this.setProduct()
  },

  methods: {

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (
        !this.liquid.handle ||
        (this.$variable('storybook') && this.handle === false)
      ) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.localHandle) {
      if (this.controller.abort) {
        this.controller.abort()
      }

      if (handle === false || this.product.handle === handle) {
        return
      }

      try {
        this.state = 'loading'
        this.controller = new AbortController()

        const response = await cnvs.Query({
          query: productQuery,
          signal: this.controller.signal,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        if (this.$variable('settings.disableComponentReadyState')) {
          return
        }

        const formattedProduct = formatProduct(response)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.updateProductUrl(formattedProduct.handle)
        this.state = 'ready'

      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }

        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0]

      if (oldHandle === handle) {
        return
      }

      const pathname = location.pathname.replace(oldHandle, handle)
      const url = `${location.origin}${pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Scroll to product-details section
     */
    scrollToSection() {
      const section = document.getElementById('product-details')

      if (!section) {
        return
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-dev-file';
</style>
